/**
 * useTransferData — data-loading hook for TransferVerification.
 * Manages transfer list, history, week navigation, and tab mode.
 * Extracted from TransferVerification.tsx for readability.
 */
import { useState, useEffect } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import {
  inventoryService,
  UnprocessedTransfer,
  VerifiedTransfer,
} from '../../../services/inventoryService';
import toast from 'react-hot-toast';

// Helper to get start of week (Monday) for a given date - uses local timezone
export const getWeekStart = (date: Date): Date => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d;
};

// Get the week start date for a given offset (0 = this week, 1 = last week, etc.)
export const getWeekStartForOffset = (weekOffset: number): Date => {
  const today = new Date();
  const weekStart = getWeekStart(today);
  weekStart.setDate(weekStart.getDate() - (weekOffset * 7));
  return weekStart;
};

// Format date range for display
export const formatWeekRange = (weekOffset: number): string => {
  const weekStart = getWeekStartForOffset(weekOffset);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const formatShort = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${formatShort(weekStart)} - ${formatShort(weekEnd)}`;
};

// Format timestamp for display
// The database server stores timestamps in UTC, but CURRENT_TIMESTAMP uses server's local time
// The cloud DB server appears to be 5 hours ahead (likely UTC), so we need to adjust
export const formatVerifiedTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  date.setHours(date.getHours() - 5);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

export type RangeDays = 1 | 7 | 14;

type TabMode = 'verify' | 'history';

interface UseTransferDataParams {
  step: string;
}

export interface UseTransferDataReturn {
  // Transfer list
  transfers: UnprocessedTransfer[];
  setTransfers: React.Dispatch<React.SetStateAction<UnprocessedTransfer[]>>;
  loadingTransfers: boolean;
  weekOffset: number;
  setWeekOffset: React.Dispatch<React.SetStateAction<number>>;
  rangeDays: RangeDays;
  setRangeDays: React.Dispatch<React.SetStateAction<RangeDays>>;
  loadTransfers: () => Promise<void>;

  // History
  historyTransfers: VerifiedTransfer[];
  loadingHistory: boolean;
  historyPage: number;
  setHistoryPage: React.Dispatch<React.SetStateAction<number>>;
  historyTotalPages: number;
  expandedHistoryTransfer: string | null;
  setExpandedHistoryTransfer: React.Dispatch<React.SetStateAction<string | null>>;
  expandedHistoryBoxes: Set<string>;
  setExpandedHistoryBoxes: React.Dispatch<React.SetStateAction<Set<string>>>;
  loadHistory: () => Promise<void>;

  // Tab mode
  tabMode: TabMode;
  setTabMode: React.Dispatch<React.SetStateAction<TabMode>>;
}

export function useTransferData({ step }: UseTransferDataParams): UseTransferDataReturn {
  const { t } = useLanguage();

  // Transfer list state
  const [transfers, setTransfers] = useState<UnprocessedTransfer[]>([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [rangeDays, setRangeDays] = useState<RangeDays>(1);

  // Tab mode
  const [tabMode, setTabMode] = useState<TabMode>('verify');

  // History state
  const [historyTransfers, setHistoryTransfers] = useState<VerifiedTransfer[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [expandedHistoryTransfer, setExpandedHistoryTransfer] = useState<string | null>(null);
  const [expandedHistoryBoxes, setExpandedHistoryBoxes] = useState<Set<string>>(new Set());

  const loadTransfers = async () => {
    setLoadingTransfers(true);
    try {
      let data: UnprocessedTransfer[];
      if (rangeDays === 7) {
        // Use existing weekOffset-based API
        data = await inventoryService.getUnprocessedTransfers(weekOffset);
      } else {
        // Compute dateFrom/dateTo based on rangeDays and weekOffset
        const toYYYYMMDD = (d: Date) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${y}${m}${day}`;
        };
        const baseDate = new Date();
        if (rangeDays === 1) {
          baseDate.setDate(baseDate.getDate() - weekOffset);
          const dateStr = toYYYYMMDD(baseDate);
          data = await inventoryService.getUnprocessedTransfers(0, dateStr, dateStr);
        } else {
          // 14 days — 2-week window
          const weekStart = getWeekStart(baseDate);
          weekStart.setDate(weekStart.getDate() - (weekOffset * 14));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 13);
          data = await inventoryService.getUnprocessedTransfers(0, toYYYYMMDD(weekStart), toYYYYMMDD(weekEnd));
        }
      }
      setTransfers(data);
    } catch (err) {
      toast.error(t('transferVerification.loadError'));
    } finally {
      setLoadingTransfers(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await inventoryService.getVerificationHistory({ page: historyPage, limit: 20 });
      setHistoryTransfers(data.transfers);
      setHistoryTotalPages(data.pagination.total_pages);
    } catch (err) {
      toast.error(t('transferVerification.historyLoadError'));
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load unprocessed transfers when entering transfer step or range changes
  useEffect(() => {
    if (step === 'transfer' && tabMode === 'verify') {
      loadTransfers();
    }
  }, [step, weekOffset, rangeDays, tabMode]);

  // Load history when switching to history tab
  useEffect(() => {
    if (tabMode === 'history') {
      loadHistory();
    }
  }, [tabMode, historyPage]);

  return {
    transfers,
    setTransfers,
    loadingTransfers,
    weekOffset,
    setWeekOffset,
    rangeDays,
    setRangeDays,
    loadTransfers,
    historyTransfers,
    loadingHistory,
    historyPage,
    setHistoryPage,
    historyTotalPages,
    expandedHistoryTransfer,
    setExpandedHistoryTransfer,
    expandedHistoryBoxes,
    setExpandedHistoryBoxes,
    loadHistory,
    tabMode,
    setTabMode,
  };
}
